import {composeCellKey} from '../provider/cell-key'
import type {BoardCardAddress, CellAddress} from '../types'
import type {Active, Over} from './dnd-kit'

/**
 * What a dragged card carries, what the cell under it carries, and how to read either
 * back.
 *
 * Not exported from `board-core.ts`, deliberately. A consumer handed these could reach
 * into the drag context and couple itself to dnd-kit round the back, which is the thing
 * the wrapper exists to prevent.
 *
 * Nothing here imports React, so every reader is callable from a test with no live
 * context around it, the way `packages/workstreams-viewer/components/board/board-dnd.ts`
 * keeps its own.
 */

/** Marks a draggable's payload as a board card rather than any other draggable. */
export const BOARD_CARD_DRAG_TYPE = 'board-card'

/**
 * The payload a draggable card carries: which card, and which cell it started in.
 *
 * A {@link BoardCardAddress} with a tag on it, because a board sits inside a caller's own
 * tree and anything else in there may be draggable too. The tag is how a payload that is
 * not a card is told from one that is.
 */
export type BoardCardDragData = BoardCardAddress & {
  type: typeof BOARD_CARD_DRAG_TYPE
}

/**
 * Reads a card payload off a dnd-kit `active`, or `null` when it is not one.
 *
 * Never throws and asserts nothing. The payload arrives from whatever registered the
 * draggable, so a missing one, a foreign one and one that is not an object at all are
 * all answers rather than failures.
 */
export function readBoardCardDragData(active: Active | null | undefined): BoardCardDragData | null {
  const data = active?.data.current as Partial<BoardCardDragData> | undefined
  if (data?.type !== BOARD_CARD_DRAG_TYPE) return null

  const {columnKey, rowKey, itemId} = data
  if (typeof columnKey !== 'string' || typeof rowKey !== 'string' || typeof itemId !== 'string') return null

  return {type: BOARD_CARD_DRAG_TYPE, columnKey, rowKey, itemId}
}

/** Marks a droppable's payload as a board cell rather than any other droppable. */
export const BOARD_CELL_DROP_TYPE = 'board-cell'

/**
 * The attribute a drawn card carries its item id in.
 *
 * The DOM half of the same statement {@link BOARD_CARD_DRAG_TYPE} makes about a payload:
 * this element is one of the board's cards, and this is which card it is. `useBoardCard`
 * puts it on every card it draws, and `useBoardCell` reads it back to find which of its
 * cards are in the page and where each one sits on screen.
 *
 * An attribute rather than a registry of nodes, because what a cell needs is the drawn run
 * in the order it is drawn, and the document already holds exactly that. A cell windows its
 * cards, so the cards carrying this are the ones a reader can see and no others, which is
 * what makes a drop resolve against what somebody is aiming at.
 *
 * The card's `id` names the same card, and cannot be used for this: it is spelt out of a
 * `useId` the card generates for itself, so nothing outside that card can compose it.
 */
export const BOARD_CARD_ID_ATTRIBUTE = 'data-board-card-id'

/**
 * The payload a cell registers as a drop target: which cell a drop would land in.
 *
 * A {@link CellAddress} tagged the way {@link BoardCardDragData} is, and for the same
 * reason: a board sits inside a caller's own tree and anything else in there may be a
 * drop target too.
 *
 * A cell and nothing smaller. No card registers a target of its own, so a drop resolves
 * to an intersection and the position inside it is a separate question.
 */
export type BoardCellDropData = CellAddress & {
  type: typeof BOARD_CELL_DROP_TYPE
}

/**
 * The id a cell registers its drop target under.
 *
 * The prefix is not there to keep a cell apart from a card. dnd-kit holds draggables and
 * droppables in two registries, `draggableNodes` and `droppableContainers`, so a droppable
 * id equal to a card's drag node id cannot reach it. The registry a cell really shares is
 * the one holding every droppable the caller registered in its own tree, and
 * `composeCellKey` is spelled out of the caller's own column and row keys, so an
 * unprefixed id is one that caller could produce by accident.
 */
export function boardCellDropId(address: CellAddress): string {
  return `${BOARD_CELL_DROP_TYPE}:${composeCellKey(address)}`
}

/**
 * Reads a cell payload off a dnd-kit `over`, or `null` when it is not one.
 *
 * Never throws and asserts nothing, for the reason {@link readBoardCardDragData} does not:
 * the payload arrives from whatever registered the droppable, so a missing one, a foreign
 * one and one that is not an object at all are all answers rather than failures.
 */
export function readBoardCellDropData(over: Over | null | undefined): BoardCellDropData | null {
  const data = over?.data.current as Partial<BoardCellDropData> | undefined
  if (data?.type !== BOARD_CELL_DROP_TYPE) return null

  const {columnKey, rowKey} = data
  if (typeof columnKey !== 'string' || typeof rowKey !== 'string') return null

  return {type: BOARD_CELL_DROP_TYPE, columnKey, rowKey}
}
